import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import type { RJSFSchema } from "@rjsf/utils";
import { OutcomeForm } from "@/components/outcome-form";
import { ReviewsSection } from "@/components/reviews-section";
import { useCreateJob, useOutcome } from "@/hooks/use-catalog";
import { useFavorites, useToggleFavorite } from "@/hooks/use-favorites";
import { useWallet } from "@/hooks/use-wallet";
import { useAuth } from "@/lib/auth";
import { formatTokens } from "@/lib/utils";

export function OutcomeDetailPage() {
  const { partner_slug, outcome_slug } = useParams<{
    partner_slug: string;
    outcome_slug: string;
  }>();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const { data: outcome, isLoading } = useOutcome(partner_slug ?? "", outcome_slug ?? "");
  const { data: wallet } = useWallet(Boolean(user));
  const { data: favorites } = useFavorites();
  const isFav = !!favorites?.some((f) => f.id === outcome?.outcome_id);
  const toggleFav = useToggleFavorite(outcome?.outcome_id ?? "", isFav);
  const createJob = useCreateJob();
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="mx-auto mt-16 max-w-3xl px-4">
        <div className="h-10 w-1/2 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
        <div className="mt-6 h-48 animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-900" />
      </div>
    );
  }
  if (!outcome) {
    return (
      <div className="mx-auto mt-24 max-w-xl px-4 text-center">
        <h1 className="text-xl font-semibold">Outcome not found</h1>
        <p className="mt-2 text-sm text-neutral-500">
          It may have been retired or never approved.
        </p>
        <Link
          to="/catalog"
          className="mt-6 inline-block rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700"
        >
          Back to catalog
        </Link>
      </div>
    );
  }

  const submitting = createJob.isPending;

  async function handleSubmit(formData: Record<string, unknown>) {
    setSubmitError(null);
    if (!user) {
      navigate(`/login?return_to=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    try {
      const result = await createJob.mutateAsync({
        outcome_id: outcome!.outcome_id,
        input: formData,
      });
      navigate(`/jobs/${result.job_id}`);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
  }

  const effectiveMax = outcome.max_price_tokens ?? outcome.price_tokens;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/catalog" className="text-sm text-neutral-500 hover:underline">
        ← Catalog
      </Link>

      <header className="mt-4">
        <p className="text-sm text-neutral-500">{outcome.partner_name}</p>
        <div className="mt-1 flex items-start justify-between gap-3">
          <h1 className="text-3xl font-semibold tracking-tight">
            {outcome.name}
          </h1>
          {user ? (
            <button
              type="button"
              onClick={() => toggleFav.mutate()}
              disabled={toggleFav.isPending}
              aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
              className="shrink-0 rounded-full border border-neutral-200 p-2 text-lg transition hover:border-amber-400 dark:border-neutral-800"
            >
              <span className={isFav ? "text-amber-500" : "text-neutral-400"}>
                {isFav ? "★" : "☆"}
              </span>
            </button>
          ) : null}
        </div>
        {outcome.description ? (
          <p className="mt-3 text-neutral-600 dark:text-neutral-400">
            {outcome.description}
          </p>
        ) : null}

        <dl className="mt-6 grid grid-cols-2 gap-4 rounded-lg bg-neutral-50 p-4 text-sm dark:bg-neutral-900 sm:grid-cols-4">
          <div>
            <dt className="text-neutral-500">Price</dt>
            <dd className="font-semibold">
              {outcome.price_kind === "variable" && outcome.max_price_tokens
                ? `${formatTokens(outcome.price_tokens)}–${formatTokens(outcome.max_price_tokens)}`
                : formatTokens(outcome.price_tokens)}{" "}
              <span className="font-normal text-neutral-400">tokens</span>
            </dd>
          </div>
          <div>
            <dt className="text-neutral-500">Delivery</dt>
            <dd className="font-semibold capitalize">{outcome.delivery_mode}</dd>
          </div>
          <div>
            <dt className="text-neutral-500">Timeout</dt>
            <dd className="font-semibold">
              {Math.round(outcome.max_duration_seconds / 60)} min
            </dd>
          </div>
          {outcome.rating_count > 0 ? (
            <div>
              <dt className="text-neutral-500">Rating</dt>
              <dd className="font-semibold">
                ★ {outcome.rating_avg.toFixed(1)}{" "}
                <span className="font-normal text-neutral-400">
                  ({outcome.rating_count})
                </span>
              </dd>
            </div>
          ) : null}
        </dl>
      </header>

      <section className="mt-10 rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
        <OutcomeForm
          schema={outcome.input_schema as RJSFSchema}
          onSubmit={handleSubmit}
          submitting={submitting}
          priceTokens={outcome.price_tokens}
          maxPriceTokens={outcome.max_price_tokens}
          priceKind={outcome.price_kind}
          balance={wallet?.available ?? 0}
          isAuthenticated={Boolean(user)}
        />
        {submitError ? (
          <p className="mt-4 text-sm text-red-500">{submitError}</p>
        ) : null}
      </section>

      {user && wallet ? (
        <p className="mt-4 text-center text-xs text-neutral-500">
          You have {formatTokens(wallet.available)} tokens available
          {wallet.available < effectiveMax ? (
            <span className="text-amber-500">
              {" "}
              — not enough for this outcome.
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="mt-12">
        <ReviewsSection outcomeId={outcome.outcome_id} canReview={false} />
      </div>
    </div>
  );
}
